import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, Users, Crown } from 'lucide-react'
import { supabase } from '../../shared/api/supabase'
import { useAuth } from '../../shared/contexts/AuthContext'

interface Group {
  id: string
  name: string
  role: 'admin' | 'manager' | 'member'
}

interface GroupContextSwitcherProps {
  currentGroupId?: string
  onGroupChange?: (groupId: string) => void
  className?: string
}

export default function GroupContextSwitcher({ 
  currentGroupId, 
  onGroupChange,
  className = '' 
}: GroupContextSwitcherProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [groups, setGroups] = useState<Group[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return

    const fetchGroups = async () => {
      try {
        const { data, error } = await supabase
          .from('group_members')
          .select(`
            group_id,
            role,
            group:groups(id, name)
          `)
          .eq('user_id', user.id)

        if (error) throw error

        const groupsData: Group[] = (data || []).map((member: any) => {
          const group = Array.isArray(member.group) ? member.group[0] : member.group
          return {
            id: group.id,
            name: group.name,
            role: member.role
          }
        })

        setGroups(groupsData)
      } catch (error) {
        console.error('Error fetching groups:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGroups()
  }, [user])

  // Determine current group from URL or props
  const getCurrentGroup = () => {
    if (currentGroupId) {
      return groups.find(g => g.id === currentGroupId)
    }
    
    // Try to extract from URL
    const match = location.pathname.match(/\/groups\/([^/]+)/)
    if (match) {
      return groups.find(g => g.id === match[1])
    }
    
    return groups[0] // Default to first group
  }

  const currentGroup = getCurrentGroup()

  const handleGroupSelect = (groupId: string) => {
    setIsOpen(false)
    if (onGroupChange) {
      onGroupChange(groupId)
    } else {
      navigate(`/groups/${groupId}`)
    }
  }

  if (loading || groups.length === 0) {
    return null
  }

  // Don't show if user only has one group
  if (groups.length <= 1) {
    return null
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        <Users className="w-4 h-4" />
        <span className="max-w-[150px] truncate">
          {currentGroup?.name || 'Select Group'}
        </span>
        {currentGroup?.role === 'admin' && (
          <Crown className="w-3 h-3 text-salt-gold" />
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
            <div className="p-2">
              {groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleGroupSelect(group.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    currentGroup?.id === group.id
                      ? 'bg-salt-gold/15 dark:bg-salt-gold/10 text-salt-navy dark:text-salt-gold'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Users className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 truncate font-medium">{group.name}</span>
                  {group.role === 'admin' && (
                    <Crown className="w-3 h-3 text-salt-gold flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

